import { FC, useState, useEffect, useCallback, Suspense } from 'react';
import { View, DictionaryType } from '../../types';
import { useAuth } from '../../services/auth';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';
import { useAppSettings } from '../../hooks/queries';
import { OWNER_UID } from '../../constants';
// Components
import { SplashScreen } from './SplashScreen';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { AppRouter } from '../navigation/AppRouter';
import OrganizationSetupModal from '../auth/OrganizationSetupModal';
import ConfirmationModal from '../shared/ConfirmationModal';
import { ForcePasswordChange } from '../auth/ForcePasswordChange';
import { AutoTopUpRunner } from '../shared/AutoTopUpRunner';
// Вспомогательный компонент для аналитики
import { AnalyticsRouter } from '../analytics/AnalyticsRouter';

// Helper for loading spinner
const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-full w-full">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
    </div>
);

export const AppLayout: FC = () => {
    const [currentView, setCurrentView] = useState<View>('DASHBOARD');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [waybillToOpen, setWaybillToOpen] = useState<string | null>(null);
    const [dictionarySubView, setDictionarySubView] = useState<DictionaryType | null>(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

    // SubView handling (generic)
    const [subView, setSubView] = useState<string | null>(null);

    const onWaybillOpened = useCallback(() => {
        setWaybillToOpen(null);
    }, []);

    // Sync Firebase Auth with Generic Auth
    const { user: firebaseUser, logout: firebaseLogout, license, memberRole, memberRecord } = useFirebaseAuth();
    const { loginAs, currentUser } = useAuth(); // removed hasRole unused

    useEffect(() => {
        // If Firebase has a user but Generic Auth doesn't (or mismatch), sync it.
        if (firebaseUser) {
            if (currentUser?.id !== firebaseUser.uid) {
                // real role should come from DB/Claims
                const isSystemOwner = firebaseUser.uid === OWNER_UID;
                const role = (license || isSystemOwner || memberRole === 'admin') ? 'admin' : (memberRole || 'user');

                loginAs({
                    id: firebaseUser.uid,
                    role: role as any,
                    displayName: firebaseUser.displayName || firebaseUser.email || 'User'
                });
            }
        } else {
            // Optional: Logout generic auth if firebase is out
        }
    }, [firebaseUser, currentUser, loginAs, license, memberRole]);

    // Use logout hook from context
    const { logout } = useFirebaseAuth();

    // Settings
    const { data: appSettings, isLoading: isSettingsLoading } = useAppSettings();
    const [minLoadFinished, setMinLoadFinished] = useState(false);

    useEffect(() => {
        const minLoadTime = new Promise(resolve => setTimeout(resolve, 1300));
        minLoadTime.then(() => setMinLoadFinished(true));
    }, []);

    const handleNavigate = (view: View, subViewArg?: string) => {
        if (subViewArg) setSubView(subViewArg);
        else setSubView(null);

        if (view === 'DICTIONARIES' && subViewArg) {
            setDictionarySubView(subViewArg as DictionaryType);
        } else {
            setDictionarySubView(null);
        }
        setCurrentView(view);
    };

    const handleNavigateToWaybill = (waybillId: string) => {
        setWaybillToOpen(waybillId);
        setCurrentView('WAYBILLS');
    };

    const logoSrc = appSettings?.customLogo || './logo.svg';

    if (!minLoadFinished || isSettingsLoading) {
        return <SplashScreen />;
    }

    // INTERCEPT: Forced Password Change
    if (memberRecord?.requiresPasswordChange) {
        return <ForcePasswordChange />;
    }

    return (
        <div className="flex h-screen bg-gray-100 dark:bg-gray-900 font-sans">
            <AnalyticsRouter currentView={currentView} subView={subView} />
            <AutoTopUpRunner />
            <OrganizationSetupModal />
            <ConfirmationModal
                isOpen={isLogoutConfirmOpen}
                onClose={() => setIsLogoutConfirmOpen(false)}
                onConfirm={async () => {
                    await logout();
                    setIsLogoutConfirmOpen(false);
                }}
                title="Выйти из аккаунта?"
                message="Вы уверены, что хотите выйти из приложения?"
                confirmText="Выйти"
            />

            <Sidebar
                isSidebarOpen={isSidebarOpen}
                currentView={currentView}
                logoSrc={logoSrc}
                isHelpOpen={isHelpOpen}
                setIsHelpOpen={setIsHelpOpen}
                onNavigate={(view) => handleNavigate(view)}
            />

            <div className="flex-1 flex flex-col overflow-hidden">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    currentView={currentView}
                    onNavigate={handleNavigate}
                />

                <main className={`flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900 ${currentView === 'OWNER_PANEL' ? 'p-0' : 'p-4 sm:p-6 lg:p-8'}`}>
                    <Suspense fallback={<LoadingSpinner />}>
                        <AppRouter
                            currentView={currentView}
                            waybillToOpen={waybillToOpen}
                            onWaybillOpened={onWaybillOpened}
                            subView={subView}
                            dictionarySubView={dictionarySubView}
                            setDictionarySubView={setDictionarySubView}
                            handleNavigate={handleNavigate}
                            handleNavigateToWaybill={handleNavigateToWaybill}
                        />
                    </Suspense>
                </main>
            </div>
        </div>
    );
};
